/**
 * Move timing for the duel (duel v3 P1, docs/design/combat/2026-10-11-duel-v3-plan.md): the seconds after a move lands in
 * which its user cannot guard, dodge or press another move (systems/duelRecovery.ts). A move's own `SkillDef.recovery`
 * wins; otherwise the default is by kind and weight. A move that connected (a hit, a guarded hit, a counter) takes the
 * `hit` figure; one that found nothing (a miss, a dodge, no one in reach) takes `whiff`. A break that whiffs is the
 * longest of all: that is the gap to punish.
 */
import type { SkillDef } from './skills';

export interface MoveTiming {
  /** Recovery after the move connected (s). */
  hit: number;
  /** Recovery after it found nothing (s). */
  whiff: number;
}

/** Default recovery by kind; `attack` is scaled by weight (`attackByPower`). */
export const MOVE_TIMING = {
  break: { hit: 0.35, whiff: 0.6 },
  power: { hit: 0.5, whiff: 0.7 },
  heal: { hit: 0.2, whiff: 0.2 },
  buff: { hit: 0.1, whiff: 0.1 },
} as const satisfies Record<string, MoveTiming>;

/** Attacks: light, medium and heavy by `power` (up to 1.1, up to 1.6, above); a whiff costs half as much again. */
export const ATTACK_TIMING = { lightMax: 1.1, mediumMax: 1.6, light: 0.2, medium: 0.3, heavy: 0.4, whiffMult: 1.5 };

/** Seconds of recovery after `s` landed (`whiffed`: found nothing). */
export function recoveryOf(s: SkillDef, whiffed: boolean): number {
  if (s.recovery !== undefined) return whiffed ? s.recovery * (s.kind === 'attack' ? ATTACK_TIMING.whiffMult : 1.5) : s.recovery;
  if (s.kind === 'attack') {
    const A = ATTACK_TIMING;
    const base = s.power <= A.lightMax ? A.light : s.power <= A.mediumMax ? A.medium : A.heavy;
    return whiffed ? base * A.whiffMult : base;
  }
  const t = MOVE_TIMING[s.kind];
  return whiffed ? t.whiff : t.hit;
}
